import { useEffect, useRef, useState } from 'react';
import * as Y from 'yjs';
import { HocuspocusProvider } from '@hocuspocus/provider';
import { IndexeddbPersistence } from 'y-indexeddb';
import {
  applyCreateNodeTransaction,
  applyDeleteSubtreeTransaction,
  applyMoveNodesTransaction,
  applyReparentTransaction,
  beginUndoCaptureInteraction,
  endUndoCaptureInteraction,
  stripTransientNodeFields,
} from '../../utils/mindmap/yjsTransactions';
import { createYjsHistoryActions } from '../../utils/mindmap/yjsHistory';
import {
  createAwarenessState,
  filterAwarenessStates,
} from '../../utils/mindmap/awareness';
import {
  registerOfflineYjsDatabase,
} from '../../utils/mindmap/offlinePersistence';
import {
  MAX_CONFLICTED_NODES,
  buildConflictRecovery,
  buildRejectedAccessRecovery,
  clearOfflineNodeProposals,
  findLostNodeProposals,
  isRejectedCollaborationClose,
  readOfflineNodeProposals,
  saveOfflineNodeProposal,
  saveOfflineRecovery,
} from '../../utils/mindmap/offlineRecovery';
import {
  buildCollaborationCredential,
  getShareToken,
} from '../../utils/mindmap/apiClient';

const getDefaultWsUrl = () => 'ws://localhost:4000/ws';
const relKey = (parentId, childId) => `${parentId}:${childId}`;

function buildSnapshot(yNodes, yRels, yMeta) {
  const nodesJson = yNodes.toJSON();
  const relsJson = yRels.toJSON();
  const metaJson = yMeta.toJSON();

  return {
    nodes: Object.entries(nodesJson).map(([id, data]) => ({ id, ...data })),
    relationships: Object.values(relsJson),
    root: metaJson.root || null,
    meta: metaJson,
  };
}

export function useYjsMap({
  mindmapId,
  initialData,
  authToken,
  readOnly = false,
  currentUserId = null,
  currentUserName = null,
  selectedNodeId = null,
  editingNodeId = null,
}) {
  const [state, setState] = useState(null);
  const [isSynced, setIsSynced] = useState(false);
  const [isLocallySynced, setIsLocallySynced] = useState(false);
  const [isNetworkSynced, setIsNetworkSynced] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [actions, setActions] = useState(null);
  const [awarenessStates, setAwarenessStates] = useState([]);
  const [recoveryNotice, setRecoveryNotice] = useState(null);
  const docRef = useRef(null);
  const providerRef = useRef(null);
  const undoManagerRef = useRef(null);
  const undoInteractionRef = useRef(null);
  const networkSyncedRef = useRef(false);
  const offlineNodeProposalsRef = useRef(new Map());
  const proposalWritesRef = useRef(Promise.resolve());
  const dirtyProposalIdsRef = useRef(new Set());
  const proposalFlushTimerRef = useRef(null);
  const accessRejectedRef = useRef(false);

  // throttle state updates
  const pendingRef = useRef(false);

  useEffect(() => {
    if (!mindmapId) {
      docRef.current = null;
      providerRef.current = null;
      undoManagerRef.current = null;
      undoInteractionRef.current = null;
      setState(null);
      setIsSynced(false);
      setIsLocallySynced(false);
      setIsNetworkSynced(false);
      setConnectionStatus('disconnected');
      setActions(null);
      setAwarenessStates([]);
      setRecoveryNotice(null);
      networkSyncedRef.current = false;
      offlineNodeProposalsRef.current.clear();
      proposalWritesRef.current = Promise.resolve();
      dirtyProposalIdsRef.current.clear();
      clearTimeout(proposalFlushTimerRef.current);
      proposalFlushTimerRef.current = null;
      accessRejectedRef.current = false;
      return;
    }

    // Reset sync flag when connecting to a (possibly new) document
    setIsSynced(false);
    setIsLocallySynced(false);
    setIsNetworkSynced(false);
    setConnectionStatus('connecting');
    setRecoveryNotice(null);
    networkSyncedRef.current = false;
    offlineNodeProposalsRef.current.clear();
    proposalWritesRef.current = Promise.resolve();
    dirtyProposalIdsRef.current.clear();
    clearTimeout(proposalFlushTimerRef.current);
    proposalFlushTimerRef.current = null;
    accessRejectedRef.current = false;

    const doc = new Y.Doc();
    const yNodes = doc.getMap('nodes');
    const yRels = doc.getMap('relationships');
    const yMeta = doc.getMap('meta');
    const name = mindmapId; // IMPORTANT: keep this aligned with backend
    const wsUrl = process.env.REACT_APP_YJS_WS_URL || getDefaultWsUrl();
    const offlineDatabaseName = registerOfflineYjsDatabase(
      currentUserId,
      mindmapId
    );
    let persistence = null;
    if (offlineDatabaseName && typeof window !== 'undefined' && window.indexedDB) {
      try {
        persistence = new IndexeddbPersistence(offlineDatabaseName, doc);
      } catch {
        persistence = null;
      }
    }
    const localPersistenceReady = persistence
      ? new Promise((resolve) => persistence.once('synced', resolve))
      : Promise.resolve();

    const provider = new HocuspocusProvider({
      url: wsUrl,
      name,
      document: doc,
      token: buildCollaborationCredential(authToken, getShareToken()),
      onStatus: ({ status }) => {
        setConnectionStatus(status);
        if (status !== 'connected') {
          networkSyncedRef.current = false;
          setIsNetworkSynced(false);
        }
      },
      onClose: ({ event } = {}) => {
        if (
          !isRejectedCollaborationClose(event)
          || accessRejectedRef.current
          || !currentUserId
        ) return;
        accessRejectedRef.current = true;
        networkSyncedRef.current = false;
        const recovery = buildRejectedAccessRecovery({
          userId: currentUserId,
          mindmapId,
          snapshot: buildSnapshot(yNodes, yRels, yMeta),
        });
        setRecoveryNotice(recovery);
        void saveOfflineRecovery(recovery).catch(() => false);
        providerRef.current?.disconnect();
        setConnectionStatus('access-rejected');
        setIsNetworkSynced(false);
      },
      onAwarenessChange: ({ states }) => {
        setAwarenessStates(filterAwarenessStates(states));
      },
    });
    const initialAwarenessState = createAwarenessState({
      userId: currentUserId,
      displayName: currentUserName,
    });
    if (initialAwarenessState) {
      provider.awareness?.setLocalState(initialAwarenessState);
    } else {
      // Anonymous link visitors collaborate without claiming an account
      // identity or publishing a presence profile.
      provider.awareness?.setLocalState(null);
    }

    docRef.current = doc;
    providerRef.current = provider;

    // Create an UndoManager for collaborative undo/redo across the
    // nodes and relationships maps. This tracks changes at the Yjs
    // level so all collaborators see a consistent history.
    const undoManager = new Y.UndoManager([yNodes, yRels]);
    undoManagerRef.current = undoManager;
    const historyActions = createYjsHistoryActions(() => undoManagerRef.current);

    const scheduleUpdate = () => {
      if (pendingRef.current) return;
      pendingRef.current = true;
      queueMicrotask(() => {
        pendingRef.current = false;
        setState(buildSnapshot(yNodes, yRels, yMeta));
      });
    };

    // observe changes
    yNodes.observe(scheduleUpdate);
    yRels.observe(scheduleUpdate);
    yMeta.observe(scheduleUpdate);

    // initial render
    setState(buildSnapshot(yNodes, yRels, yMeta));

    const acceptNetworkState = () => {
      setIsSynced(true);
      if (!initialData || readOnly) return;
      doc.transact(() => {
        if (yNodes.size !== 0 || yRels.size !== 0) return;

        // seed nodes
        if (Array.isArray(initialData.nodes)) {
          for (const node of initialData.nodes) {
            if (!node?.id) continue;
            const { id, ...rest } = stripTransientNodeFields(node);
            yNodes.set(id, rest);
          }
        }

        // seed relationships
        if (Array.isArray(initialData.relationships)) {
          for (const rel of initialData.relationships) {
            if (!rel?.parentId || !rel?.childId) continue;
            yRels.set(relKey(rel.parentId, rel.childId), {
              parentId: rel.parentId,
              childId: rel.childId,
            });
          }
        }

        // seed root only (NOT selectedId)
        if (initialData.root) yMeta.set('root', initialData.root);

        // seed any extra meta keys (avoid root/selectedId)
        if (initialData.meta && typeof initialData.meta === 'object') {
          for (const [k, v] of Object.entries(initialData.meta)) {
            if (k === 'root' || k === 'selectedId') continue;
            yMeta.set(k, v);
          }
        }
      });

      scheduleUpdate();
    };

    const persistedProposalsPromise = currentUserId
      ? readOfflineNodeProposals(currentUserId, mindmapId).catch(() => new Map())
      : Promise.resolve(new Map());
    let networkSyncInFlight = false;
    let disposed = false;

    const flushOfflineProposals = () => {
      clearTimeout(proposalFlushTimerRef.current);
      proposalFlushTimerRef.current = null;
      if (!currentUserId || !dirtyProposalIdsRef.current.size) return;
      const dirtyNodeIds = [...dirtyProposalIdsRef.current];
      dirtyProposalIdsRef.current.clear();
      const proposals = dirtyNodeIds
        .map((nodeId) => offlineNodeProposalsRef.current.get(nodeId))
        .filter(Boolean);
      proposalWritesRef.current = proposalWritesRef.current
        .catch(() => false)
        .then(async () => {
          for (const node of proposals) {
            await saveOfflineNodeProposal({
              userId: currentUserId,
              mindmapId,
              node,
            });
          }
        })
        .catch(() => false);
    };

    const scheduleProposalFlush = () => {
      clearTimeout(proposalFlushTimerRef.current);
      proposalFlushTimerRef.current = setTimeout(flushOfflineProposals, 200);
    };

    const onNetworkSynced = async ({ state: syncedState } = {}) => {
      if (syncedState === false || networkSyncInFlight) return;
      networkSyncInFlight = true;
      const [persistedProposals] = await Promise.all([
        persistedProposalsPromise,
        localPersistenceReady,
      ]);
      for (const [nodeId, proposal] of persistedProposals) {
        if (!offlineNodeProposalsRef.current.has(nodeId)) {
          offlineNodeProposalsRef.current.set(nodeId, proposal);
        }
      }
      if (disposed || accessRejectedRef.current) {
        networkSyncInFlight = false;
        return;
      }
      networkSyncedRef.current = true;
      flushOfflineProposals();
      await proposalWritesRef.current.catch(() => false);
      if (disposed || accessRejectedRef.current) {
        networkSyncInFlight = false;
        return;
      }
      acceptNetworkState();
      const snapshot = buildSnapshot(yNodes, yRels, yMeta);
      const conflicts = findLostNodeProposals(
        offlineNodeProposalsRef.current,
        snapshot.nodes
      );
      offlineNodeProposalsRef.current.clear();
      if (conflicts.length && currentUserId) {
        const recovery = buildConflictRecovery({
          userId: currentUserId,
          mindmapId,
          conflicts,
          snapshot,
        });
        setRecoveryNotice(recovery);
        void saveOfflineRecovery(recovery).catch(() => false);
      }
      if (currentUserId) {
        await clearOfflineNodeProposals(currentUserId, mindmapId).catch(() => false);
      }
      if (!disposed) setIsNetworkSynced(true);
      networkSyncInFlight = false;
    };
    const onLocalSynced = () => {
      if (disposed) return;
      setIsLocallySynced(true);
      if (yNodes.size > 0 || yRels.size > 0 || yMeta.size > 0) {
        setIsSynced(true);
        scheduleUpdate();
      }
    };

    provider.on('synced', onNetworkSynced);
    void localPersistenceReady.then(onLocalSynced);

    const rememberOfflineProposal = (nodeId) => {
      if (networkSyncedRef.current || !nodeId) return;
      const value = yNodes.get(nodeId);
      if (value) {
        offlineNodeProposalsRef.current.delete(nodeId);
        if (offlineNodeProposalsRef.current.size >= MAX_CONFLICTED_NODES) {
          const oldestNodeId = offlineNodeProposalsRef.current.keys().next().value;
          offlineNodeProposalsRef.current.delete(oldestNodeId);
        }
        offlineNodeProposalsRef.current.set(nodeId, { id: nodeId, ...value });
        if (currentUserId) {
          dirtyProposalIdsRef.current.add(nodeId);
          scheduleProposalFlush();
        }
      }
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('pagehide', flushOfflineProposals);
    }

    // Expose a stable set of actions bound to this document.
    setActions({
      setRoot(rootId) {
        if (readOnly) return false;
        doc.transact(() => yMeta.set('root', rootId));
        return true;
      },

      setTheme(themeId) {
        if (readOnly) return false;
        doc.transact(() => {
          if (themeId) {
            yMeta.set('theme', themeId);
          } else {
            yMeta.delete('theme');
          }
        });
        return true;
      },

      addNode(node) {
        if (readOnly) return false;
        if (!node?.id) return;
        const { id, ...rest } = stripTransientNodeFields(node);
        doc.transact(() => yNodes.set(id, rest));
        rememberOfflineProposal(id);
        return true;
      },

      updateNode(id, patch) {
        if (readOnly) return false;
        if (!id) return;
        doc.transact(() => {
          const cur = yNodes.get(id) || {};
          yNodes.set(id, stripTransientNodeFields({ ...cur, ...patch }));
        });
        rememberOfflineProposal(id);
        return true;
      },

      deleteNode(id) {
        if (readOnly) return false;
        if (!id) return;
        doc.transact(() => {
          const toDelete = new Set();

          const collect = (nodeId) => {
            if (!nodeId || toDelete.has(nodeId)) return;
            toDelete.add(nodeId);
            for (const [, rel] of yRels.entries()) {
              if (rel?.parentId === nodeId) {
                collect(rel.childId);
              }
            }
          };

          collect(id);

          for (const nodeId of toDelete) {
            yNodes.delete(nodeId);
          }

          for (const [key, rel] of yRels.entries()) {
            if (rel && (toDelete.has(rel.parentId) || toDelete.has(rel.childId))) {
              yRels.delete(key);
            }
          }
        });
        return true;
      },

      deleteSubtree(command) {
        if (readOnly) return false;
        return applyDeleteSubtreeTransaction({
          doc,
          yNodes,
          yRels,
          undoManager: undoManagerRef.current,
          command,
        });
      },

      addRelationship(parentId, childId) {
        if (readOnly) return false;
        if (!parentId || !childId) return;
        const key = relKey(parentId, childId);
        doc.transact(() => yRels.set(key, { parentId, childId }));
        return true;
      },

      createChild(command) {
        if (readOnly) return false;
        const result = applyCreateNodeTransaction({
          doc,
          yNodes,
          yRels,
          undoManager: undoManagerRef.current,
          command,
        });
        rememberOfflineProposal(command?.node?.id);
        return result;
      },

      createNodeWithRelationship(command) {
        if (readOnly) return false;
        const result = applyCreateNodeTransaction({
          doc,
          yNodes,
          yRels,
          undoManager: undoManagerRef.current,
          command,
        });
        rememberOfflineProposal(command?.node?.id);
        return result;
      },

      beginInteraction() {
        if (readOnly) return false;
        return beginUndoCaptureInteraction(
          undoManagerRef.current,
          undoInteractionRef
        );
      },

      moveNodes(command, options = {}) {
        if (readOnly) return false;
        return applyMoveNodesTransaction({
          doc,
          yNodes,
          undoManager: undoManagerRef.current,
          command,
          lastEditedBy: options.lastEditedBy || null,
          isolate: !!options.isolate,
        });
      },

      reparentSubtree(command, options = {}) {
        if (readOnly) return false;
        return applyReparentTransaction({
          doc,
          yNodes,
          yRels,
          undoManager: undoManagerRef.current,
          command,
          lastEditedBy: options.lastEditedBy || null,
          isolate: !undoInteractionRef.current,
        });
      },

      endInteraction() {
        if (readOnly) return false;
        return endUndoCaptureInteraction(undoInteractionRef);
      },

      deleteRelationship(parentId, childId) {
        if (readOnly) return false;
        const key = relKey(parentId, childId);
        doc.transact(() => yRels.delete(key));
        return true;
      },

      exportSnapshot() {
        return buildSnapshot(yNodes, yRels, yMeta);
      },

      ...(readOnly ? {
        undo: () => false,
        redo: () => false,
        canUndo: () => false,
        canRedo: () => false,
      } : historyActions),
    });

    return () => {
      disposed = true;
      if (typeof window !== 'undefined') {
        window.removeEventListener('pagehide', flushOfflineProposals);
      }
      flushOfflineProposals();
      endUndoCaptureInteraction(undoInteractionRef);
      provider.off('synced', onNetworkSynced);

      yNodes.unobserve(scheduleUpdate);
      yRels.unobserve(scheduleUpdate);
      yMeta.unobserve(scheduleUpdate);

      provider.destroy();
      doc.destroy();

      undoManagerRef.current = null;
      providerRef.current = null;
      docRef.current = null;
      setActions(null);
      setAwarenessStates([]);
    };
  }, [
    mindmapId,
    initialData,
    authToken,
    readOnly,
    currentUserId,
    currentUserName,
  ]);

  useEffect(() => {
    const presence = createAwarenessState({
      userId: currentUserId,
      displayName: currentUserName,
      selectedNodeId,
      editingNodeId,
    });
    if (!providerRef.current?.awareness) return undefined;
    if (!presence) {
      providerRef.current.awareness.setLocalState(null);
      return undefined;
    }

    const timer = setTimeout(() => {
      providerRef.current?.awareness?.setLocalState(presence);
    }, 100);
    return () => clearTimeout(timer);
  }, [
    currentUserId,
    currentUserName,
    selectedNodeId,
    editingNodeId,
    mindmapId,
  ]);

  return {
    yState: state,
    actions,
    yDoc: docRef.current,
    yProvider: providerRef.current,
    awarenessStates,
    connectionStatus,
    isLocallySynced,
    isNetworkSynced,
    isSynced,
    recoveryNotice,
    clearRecoveryNotice: () => setRecoveryNotice(null),
  };
}
